/**
 * Chase-on-file demo — the two-panel chase demo.
 *
 * Layout split:
 *   - LEFT panel (50% of viewport, x: 0 → w/2): real DOM inputs overlay on
 *     top of the canvas. Inputs include product name, description, price,
 *     and 6 thumbnail buttons for the product images. ACTIVATE button at
 *     bottom. Built in plain HTML/CSS so they actually receive keystrokes
 *     and clicks reliably (we learned from the failed cssIngest approach
 *     earlier in this session — input events on canvas-rendered DOM proxies
 *     are unreliable).
 *
 *   - RIGHT panel (50% of viewport, x: w/2 → w): rendered on the canvas
 *     using the native SceneNode API (makeRect / makeText / makeImage /
 *     addNode). Shows:
 *       - black background
 *       - the currently-selected dress photo with a continuous Ken Burns
 *         zoom (1.0 → 1.15 over 12 seconds, looping)
 *       - a soft bottom gradient overlay for caption legibility
 *       - a small "CHASE · LIVE" badge top-left
 *       - caption block bottom-left with three text SceneNodes:
 *           caption-name (large), caption-desc (medium), caption-price
 *           (orange accent)
 *
 *   - ACTIVATE button: flashes the right-panel image (240ms fade) and
 *     resets the Ken Burns cycle so the photo "snaps back" from the
 *     zoomed-in state, then continues.
 *
 * Live-update wiring:
 *   - DOM input "input" events mutate the corresponding SceneNode.text
 *     field. The engine recompiles every frame; the text cache re-bakes
 *     the glyph texture as soon as the cache key changes (keyOf includes
 *     the node.text, see textLayout.ts).
 *   - Thumbnail click handlers mutate the SceneNode.imageUrl and update
 *     which thumbnail shows the orange "active" border.
 *
 * The point of this demo: change the product name on the left, watch
 * the right panel's caption update on the next frame. No re-render,
 * no transition, no waiting.
 */

import {
  type Scene,
  type SceneNode,
  createScene,
  makeRect,
  makeText,
  makeImage,
  addNode,
} from "../sceneGraph";

// ─────────────────────────────────────────────────────────────────────────
// Six real product photos from the Thérèse dress product page on tnuck.com.
// Vite serves anything in /public/ at the site root, so these URLs work
// both locally (localhost:3000) and through the cloudflared tunnel.
// ─────────────────────────────────────────────────────────────────────────
const DRESS_PHOTOS = [
  "/dress/1685340_01.jpg",
  "/dress/1685340_02.jpg",
  "/dress/1685340_03.jpg",
  "/dress/1685340_04.jpg",
  "/dress/1685340_05.jpg",
  "/dress/1685340_06.jpg",
];

// Default product card content. The right panel starts with these values;
// the DOM inputs and their handlers can rewrite them at any time.
const DEFAULT_NAME = "Thérèse Dress";
const DEFAULT_DESC = "Cotton-linen, Belle Bouquet pattern";
const DEFAULT_PRICE = "$348";
const DEFAULT_PHOTO_INDEX = 0;

const STORAGE_KEY = "nspp.chase.product";

interface StoredProduct {
  name: string;
  desc: string;
  price: string;
  photoIndex: number;
}

function loadStoredProduct(): StoredProduct | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredProduct>;
    if (typeof parsed.name !== "string") return null;
    return {
      name: parsed.name,
      desc: typeof parsed.desc === "string" ? parsed.desc : "",
      price: typeof parsed.price === "string" ? parsed.price : "",
      photoIndex:
        typeof parsed.photoIndex === "number" && parsed.photoIndex >= 0 &&
        parsed.photoIndex < DRESS_PHOTOS.length
          ? parsed.photoIndex
          : DEFAULT_PHOTO_INDEX,
    };
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// All public refs the live-wiring code uses. They're exposed on a single
// object so callers can find the right SceneNode by semantic role, not by
// fragile DOM IDs.
// ─────────────────────────────────────────────────────────────────────────
export type ChaseRefs = {
  scene: Scene;
  captionName: SceneNode;
  captionDesc: SceneNode;
  captionPrice: SceneNode;
  heroImage: SceneNode;
  kenBurns: SceneNode;
  videoPanelBg: SceneNode;
};

// ─────────────────────────────────────────────────────────────────────────
// Hero image bounds — these stay constant. The Ken Burns animation only
// animates scaleX/scaleY of the image node itself; x/y/w/h stay at the
// full right-panel rectangle.
// ─────────────────────────────────────────────────────────────────────────
const KEN_BURNS_PERIOD_MS = 12_000; // 12 second loop
const KEN_BURNS_SCALE_MIN = 1.0;
const KEN_BURNS_SCALE_MAX = 1.15;

// ─────────────────────────────────────────────────────────────────────────
// Responsive breakpoint. Below this viewport width the chase scene stacks
// vertically (form on top, photo on bottom). Above it, it uses the
// horizontal 50/50 split. iPhone 13 Pro Max portrait is 428×926 — well
// below 720 — so users on that device get the stacked layout.
// ─────────────────────────────────────────────────────────────────────────
const STACKED_BREAKPOINT = 720;

/** Pick the geometry for the chase scene + DOM overlay based on the
 *  current viewport. Returns the dimensions of each panel so the
 *  scene/overlay can position nodes and inputs consistently.
 *
 *  Horizontal (desktop / landscape): two panels side-by-side.
 *  Stacked (mobile portrait): form on top, photo below.
 */
function pickChaseLayout(w: number, h: number): {
  mode: "horizontal" | "stacked";
  leftPanel: { x: number; y: number; w: number; h: number };
  rightPanel: { x: number; y: number; w: number; h: number };
  pad: number;
  captionFontSize: number;
  captionPriceFontSize: number;
} {
  const isStacked = w < STACKED_BREAKPOINT;
  if (isStacked) {
    // Vertical: form takes top half, photo takes bottom half.
    const halfH = Math.floor(h / 2);
    return {
      mode: "stacked",
      leftPanel:  { x: 0, y: 0,        w, h: halfH },
      rightPanel: { x: 0, y: halfH,   w, h: h - halfH },
      pad: 16,
      captionFontSize: 24, // smaller on phone so it fits in the narrower
                            // right panel
      captionPriceFontSize: 16,
    };
  }
  // Horizontal: form on left half, photo on right half.
  const halfW = Math.floor(w / 2);
  return {
    mode: "horizontal",
    leftPanel:  { x: 0,      y: 0, w: halfW, h },
    rightPanel: { x: halfW,  y: 0, w: w - halfW, h },
    pad: 28,
    captionFontSize: 32,
    captionPriceFontSize: 18,
  };
}

export function sceneChase(w: number, h: number): Scene {
  const scene = createScene();

  const layout = pickChaseLayout(w, h);
  // Pull persisted values up front so the right panel reflects saved state
  // on first frame, not after a Save click.
  const stored = loadStoredProduct();
  const initialName = stored?.name ?? DEFAULT_NAME;
  const initialDesc = stored?.desc ?? DEFAULT_DESC;
  const initialPrice = stored?.price ?? DEFAULT_PRICE;
  const initialPhotoIdx = stored?.photoIndex ?? DEFAULT_PHOTO_INDEX;
  const right = layout.rightPanel;
  const panelLeft = right.x;        // x where the photo panel starts
  const panelTop  = right.y;        // y where the photo panel starts
  const panelRight = right.w;       // photo panel width
  const panelBottom = right.h;      // photo panel height
  const pad = layout.pad;

  let z = 0;
  const add = (n: SceneNode) => {
    n.zIndex = z++;
    addNode(scene, n);
  };

  // ── BACKGROUND fills the entire viewport (form panel covers its half
  //    with DOM, photo panel covers its half with the canvas image).
  add(makeRect({
    x: 0, y: 0, w, h,
    fill: { r: 0.04, g: 0.04, b: 0.05, a: 1 },
  }));

  if (layout.mode === "horizontal") {
    // Vertical divider between form and photo on desktop.
    add(makeRect({
      x: panelLeft - 1, y: 0, w: 1, h,
      fill: { r: 1, g: 1, b: 1, a: 0.06 },
    }));
  } else {
    // Horizontal divider between form (top) and photo (bottom) on mobile.
    add(makeRect({
      x: 0, y: panelTop - 1, w, h: 1,
      fill: { r: 1, g: 1, b: 1, a: 0.06 },
    }));
  }

  // ── The photo panel: full-bleed image area on the right (desktop)
  //    or bottom (mobile). Black background + the dress photo + Ken
  //    Burns animation + caption overlay.
  add(makeRect({
    x: panelLeft, y: panelTop, w: panelRight, h: panelBottom,
    fill: { r: 0, g: 0, b: 0, a: 1 },
  }));

  const heroImage = makeImage({
    x: panelLeft, y: panelTop, w: panelRight, h: panelBottom,
    imageUrl: DRESS_PHOTOS[initialPhotoIdx],
    transformOriginX: 0.5,
    transformOriginY: 0.5,
    radius: 0,
    tagName: "chase-hero-image",
    update: makeKenBurnsUpdate(),
  });
  add(heroImage);

  // Bottom gradient strip on the photo panel for caption legibility.
  add(makeRect({
    x: panelLeft,
    y: panelTop + panelBottom - Math.min(240, panelBottom * 0.6),
    w: panelRight,
    h: Math.min(240, panelBottom * 0.6),
    gradientEnd: { r: 0, g: 0, b: 0, a: 1 },
    gradientAngle: Math.PI / 2, // top-to-bottom
    fill: { r: 0, g: 0, b: 0, a: 0 },
  }));

  // ── Caption block bottom-left of photo panel ──────────────────────
  const captionName = makeText({
    x: panelLeft + pad,
    y: panelTop + panelBottom - pad - 96,
    w: panelRight - pad * 2,
    h: 44,
    text: initialName,
    fontSize: layout.captionFontSize,
    fontWeight: 800,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    textWrap: false,
    lineHeight: 1.1,
    tagName: "chase-caption-name",
  });
  add(captionName);

  const captionDesc = makeText({
    x: panelLeft + pad,
    y: panelTop + panelBottom - pad - 48,
    w: panelRight - pad * 2,
    h: 22,
    text: initialDesc,
    fontSize: 14,
    fontWeight: 500,
    textColor: { r: 1, g: 1, b: 1, a: 0.85 },
    textWrap: false,
    lineHeight: 1.4,
    tagName: "chase-caption-desc",
  });
  add(captionDesc);

  const captionPrice = makeText({
    x: panelLeft + pad,
    y: panelTop + panelBottom - pad - 18,
    w: panelRight - pad * 2,
    h: 22,
    text: initialPrice,
    fontSize: layout.captionPriceFontSize,
    fontWeight: 700,
    textColor: { r: 0.98, g: 0.57, b: 0.24, a: 1 },
    textWrap: false,
    tagName: "chase-caption-price",
  });
  add(captionPrice);

  // ── "CHASE · LIVE" badge top-left of photo panel ──────────────────
  add(makeRect({
    x: panelLeft + 16, y: panelTop + 16, w: 116, h: 28,
    radius: 14,
    fill: { r: 0, g: 0, b: 0, a: 0.6 },
  }));
  add(makeRect({
    x: panelLeft + 24, y: panelTop + 24, w: 8, h: 8,
    radius: 4,
    fill: { r: 0.94, g: 0.27, b: 0.27, a: 1 },
  }));
  add(makeText({
    x: panelLeft + 38, y: panelTop + 22, w: 92, h: 16,
    text: "CHASE · LIVE",
    fontSize: 11,
    fontWeight: 700,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    letterSpacing: 1.4,
  }));

  return scene;
}

/** Build the Ken Burns animation closure. The engine calls this with
 *  (node, t, dt) every frame. We mutate scaleX/scaleY on the image
 *  node to drive a slow, continuous zoom-in over 12 seconds, then
 *  resets and loops.
 *
 *  The cycle is `elapsed % period / period` mapped to a linear ramp
 *  from KEN_BURNS_SCALE_MIN to KEN_BURNS_SCALE_MAX, then a quick reset
 *  back to MIN at the cycle boundary.
 */
function makeKenBurnsUpdate(): (n: SceneNode, t: number, dt: number) => void {
  const startT = performance.now();
  return function (n) {
    const elapsed = (performance.now() - startT) / 1000;
    const cycleFrac = (elapsed % KEN_BURNS_PERIOD_MS) / KEN_BURNS_PERIOD_MS;
    // EaseInOut cubic so the zoom feels cinematic, not linear.
    const eased = cycleFrac * cycleFrac * (3 - 2 * cycleFrac);
    const scale = KEN_BURNS_SCALE_MIN + (KEN_BURNS_SCALE_MAX - KEN_BURNS_SCALE_MIN) * eased;
    n.scaleX = scale;
    n.scaleY = scale;
  };
}

// ─────────────────────────────────────────────────────────────────────────
// DOM overlay for the LEFT panel. Plain HTML so inputs work reliably.
// Built and mounted by `mountChaseOverlay()`, removed by
// `unmountChaseOverlay()`.
// ─────────────────────────────────────────────────────────────────────────
const OVERLAY_ID = "chase-overlay";

export function mountChaseOverlay(w: number, h: number): void {
  if (document.getElementById(OVERLAY_ID)) return; // idempotent

  const layout = pickChaseLayout(w, h);
  const left = layout.leftPanel;
  const pad = layout.pad;

  // Build the overlay element tree imperatively. Using innerHTML + ids
  // is fine for the inputs but we need real EventTarget references, so
  // we'll build each element and append.
  const root = document.createElement("div");
  root.id = OVERLAY_ID;
  root.style.cssText = `
    position: fixed;
    left: ${left.x}px; top: ${left.y}px;
    width: ${left.w}px;
    height: ${left.h}px;
    background: linear-gradient(180deg, #0a0a0c 0%, #14141a 100%);
    display: flex;
    flex-direction: column;
    padding: ${pad}px ${pad}px;
    gap: ${Math.round(pad * 0.6)}px;
    color: #fafafa;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
    z-index: 5;
    box-sizing: border-box;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `;

  // Header
  const header = document.createElement("div");
  header.style.cssText = "display: flex; flex-direction: column; gap: 4px;";
  header.innerHTML = `
    <div style="font-size: 10px; letter-spacing: 0.16em; color: rgba(255,255,255,0.55); font-weight: 600;">
      CHASE · ON FILE
    </div>
    <div style="font-size: 22px; font-weight: 800; letter-spacing: -0.01em;">
      Product card
    </div>
    <div style="font-size: 12px; color: rgba(255,255,255,0.6);">
      Edit the fields. The right panel updates live.
    </div>
  `;
  root.appendChild(header);

  // Field factory: produces a labelled contenteditable div that mimics an
  // <input>. We use contenteditable instead of <input> so the styling
  // is uniform across browsers without dealing with autofill quirks.
  function makeField(label: string, initial: string): {
    wrap: HTMLDivElement;
    el: HTMLDivElement;
  } {
    const wrap = document.createElement("div");
    wrap.style.cssText =
      "display: flex; flex-direction: column; gap: 6px;";
    const lab = document.createElement("div");
    lab.style.cssText =
      "font-size: 10px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55);";
    lab.textContent = label;
    const el = document.createElement("div");
    el.style.cssText = `
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.10);
      border-radius: 10px;
      padding: 12px 14px;
      font-size: 15px; font-weight: 600;
      color: #fafafa;
      min-height: 20px;
      outline: none;
      transition: border-color 0.15s, background 0.15s;
      font-family: inherit;
    `;
    el.contentEditable = "true";
    el.spellcheck = false;
    el.textContent = initial;
    el.addEventListener("focus", () => {
      el.style.borderColor = "rgba(251,146,60,0.6)";
      el.style.background = "rgba(255,255,255,0.08)";
    });
    el.addEventListener("blur", () => {
      el.style.borderColor = "rgba(255,255,255,0.10)";
      el.style.background = "rgba(255,255,255,0.06)";
    });
    el.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        el.blur();
      }
    });
    wrap.appendChild(lab);
    wrap.appendChild(el);
    return { wrap, el };
  }

  const nameField = makeField("PRODUCT NAME", DEFAULT_NAME);
  const descField = makeField("DESCRIPTION", DEFAULT_DESC);
  const priceField = makeField("PRICE", DEFAULT_PRICE);
  root.appendChild(nameField.wrap);
  root.appendChild(descField.wrap);
  root.appendChild(priceField.wrap);

  // Thumbnail picker (6 dress photos as small thumbnails).
  const thumbWrap = document.createElement("div");
  thumbWrap.style.cssText = "display: flex; flex-direction: column; gap: 6px;";
  const thumbLab = document.createElement("div");
  thumbLab.style.cssText =
    "font-size: 10px; font-weight: 600; letter-spacing: 0.1em; color: rgba(255,255,255,0.55);";
  thumbLab.textContent = "PRODUCT IMAGES";
  thumbWrap.appendChild(thumbLab);
  const thumbRow = document.createElement("div");
  thumbRow.style.cssText =
    "display: flex; gap: 8px; flex-wrap: wrap;";
  thumbRow.id = "chase-thumbs";

  const thumbRefs: HTMLDivElement[] = [];
  const isMobile = layout.mode === "stacked";
  const thumbW = isMobile ? 52 : 64;
  const thumbH = isMobile ? 64 : 80;
  const thumbGap = isMobile ? 6 : 8;
  thumbRow.style.gap = `${thumbGap}px`;
  DRESS_PHOTOS.forEach((url, i) => {
    const t = document.createElement("div");
    t.dataset.cfThumb = String(i);
    t.style.cssText = `
      width: ${thumbW}px; height: ${thumbH}px; border-radius: 8px;
      background-image: url('${url}');
      background-size: cover;
      background-position: center;
      border: 2px solid ${i === DEFAULT_PHOTO_INDEX
        ? "rgba(251,146,60,0.95)"
        : "rgba(255,255,255,0.08)"};
      box-shadow: ${i === DEFAULT_PHOTO_INDEX
        ? "0 0 0 3px rgba(251,146,60,0.25)"
        : "none"};
      cursor: pointer;
      flex-shrink: 0;
      transition: transform 0.15s, border-color 0.15s;
    `;
    t.addEventListener("mouseenter", () => {
      t.style.transform = "translateY(-2px)";
    });
    t.addEventListener("mouseleave", () => {
      t.style.transform = "translateY(0)";
    });
    thumbRow.appendChild(t);
    thumbRefs.push(t);
  });
  thumbWrap.appendChild(thumbRow);
  root.appendChild(thumbWrap);

  // Save controls: dirty indicator + Save button. Tracks the user's
  // pending edits; clicking Save persists name/desc/price/photoIndex to
  // localStorage. On reload the right panel restores the saved values.
  const saveRow = document.createElement("div");
  saveRow.style.cssText = `
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 12px; gap: 10px;
  `;
  const dirtyDot = document.createElement("span");
  dirtyDot.style.cssText = `
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 10px; font-weight: 600; letter-spacing: 0.08em;
    color: rgba(255,255,255,0.45);
    transition: color 0.15s;
  `;
  dirtyDot.innerHTML =
    '<span style="width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,0.25);display:inline-block;transition:background 0.15s"></span><span>Saved</span>';
  const saveBtn = document.createElement("button");
  saveBtn.textContent = "Save";
  saveBtn.style.cssText = `
    background: rgba(251,146,60,0.16);
    color: #fb923c;
    border: 1px solid rgba(251,146,60,0.4);
    border-radius: 8px;
    padding: 8px 18px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, transform 0.08s;
    font-family: inherit;
  `;
  saveBtn.addEventListener("mouseenter", () => {
    saveBtn.style.background = "rgba(251,146,60,0.28)";
    saveBtn.style.borderColor = "rgba(251,146,60,0.7)";
  });
  saveBtn.addEventListener("mouseleave", () => {
    saveBtn.style.background = "rgba(251,146,60,0.16)";
    saveBtn.style.borderColor = "rgba(251,146,60,0.4)";
  });
  saveBtn.addEventListener("mousedown", () => {
    saveBtn.style.transform = "scale(0.97)";
  });
  saveBtn.addEventListener("mouseup", () => {
    saveBtn.style.transform = "scale(1)";
  });

  // State for the dirty/clean indicator and Save button enabled state.
  // Initial values match what's in the DOM, so on first load we're clean.
  // photoIndex tracks which thumbnail is currently selected — starts at 0
  // and updates as the user clicks thumbnails; we don't have a DOM field
  // to read for this so we just start at 0 and let clicks increment.
  const state = {
    name: nameField.el.textContent ?? "",
    desc: descField.el.textContent ?? "",
    price: priceField.el.textContent ?? "",
    photoIndex: 0,
  };
  const saved = {
    name: state.name, desc: state.desc, price: state.price, photoIndex: state.photoIndex,
  };

  const setDirty = (dirty: boolean) => {
    dirtyDot.innerHTML = dirty
      ? '<span style="width:6px;height:6px;border-radius:50%;background:#fb923c;display:inline-block"></span><span style="color:#fb923c">Unsaved</span>'
      : '<span style="width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,0.25);display:inline-block"></span><span>Saved</span>';
    saveBtn.style.opacity = dirty ? "1" : "0.55";
    saveBtn.style.cursor = dirty ? "pointer" : "default";
  };
  setDirty(false);

  const isDirty = () =>
    state.name !== saved.name ||
    state.desc !== saved.desc ||
    state.price !== saved.price ||
    state.photoIndex !== saved.photoIndex;

  const onAnyChange = () => setDirty(isDirty());

  saveBtn.addEventListener("click", () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      saved.name = state.name;
      saved.desc = state.desc;
      saved.price = state.price;
      saved.photoIndex = state.photoIndex;
      setDirty(false);
      // Brief confirmation flash on the dot
      dirtyDot.innerHTML =
        '<span style="width:6px;height:6px;border-radius:50%;background:#4ade80;display:inline-block"></span><span style="color:#4ade80">Saved</span>';
      setTimeout(() => { if (!isDirty()) setDirty(false); }, 1200);
    } catch {
      dirtyDot.innerHTML =
        '<span style="width:6px;height:6px;border-radius:50%;background:#f87171;display:inline-block"></span><span style="color:#f87171">Save failed</span>';
    }
  });

  saveRow.appendChild(dirtyDot);
  saveRow.appendChild(saveBtn);
  root.appendChild(saveRow);

  // Mount
  document.body.appendChild(root);

  // Wire up the live-update handlers. We push them onto window.__chaseDemo
  // so the scene-switching code can tear them down when the user picks a
  // different scene.
  const handlers: ChaseOverlayHandlers = {
    onName: (v) => {
      window.__chaseRefs?.captionName && (window.__chaseRefs.captionName.text = v);
    },
    onDesc: (v) => {
      window.__chaseRefs?.captionDesc && (window.__chaseRefs.captionDesc.text = v);
    },
    onPrice: (v) => {
      window.__chaseRefs?.captionPrice && (window.__chaseRefs.captionPrice.text = v);
    },
    onPickImage: (i) => {
      if (window.__chaseRefs?.heroImage) {
        window.__chaseRefs.heroImage.imageUrl = DRESS_PHOTOS[i];
      }
      thumbRefs.forEach((t, j) => {
        const active = j === i;
        t.style.border = active
          ? "2px solid rgba(251,146,60,0.95)"
          : "2px solid rgba(255,255,255,0.08)";
        t.style.boxShadow = active
          ? "0 0 0 3px rgba(251,146,60,0.25)"
          : "none";
      });
    },
  };

  nameField.el.addEventListener("input", () => {
    state.name = nameField.el.textContent ?? "";
    handlers.onName(nameField.el.textContent ?? "");
    onAnyChange();
  });
  descField.el.addEventListener("input", () => {
    state.desc = descField.el.textContent ?? "";
    handlers.onDesc(descField.el.textContent ?? "");
    onAnyChange();
  });
  priceField.el.addEventListener("input", () => {
    state.price = priceField.el.textContent ?? "";
    handlers.onPrice(priceField.el.textContent ?? "");
    onAnyChange();
  });
  thumbRefs.forEach((t, i) => {
    t.addEventListener("click", () => {
      state.photoIndex = i;
      handlers.onPickImage(i);
      onAnyChange();
    });
  });

  window.__chaseOverlayHandlers = handlers;
}

export function unmountChaseOverlay(): void {
  const root = document.getElementById(OVERLAY_ID);
  if (root && root.parentElement) {
    root.parentElement.removeChild(root);
  }
  window.__chaseOverlayHandlers = undefined;
  window.__chaseRefs = undefined;
}

// ─────────────────────────────────────────────────────────────────────────
// Global handles — set when the chase scene is active, cleared when not.
// These exist so the DOM overlay's input handlers can find the live
// SceneNode refs without module-level coupling to main.ts.
// ─────────────────────────────────────────────────────────────────────────
type ChaseOverlayHandlers = {
  onName: (v: string) => void;
  onDesc: (v: string) => void;
  onPrice: (v: string) => void;
  onPickImage: (i: number) => void;
};

declare global {
  interface Window {
    __chaseRefs?: ChaseRefs;
    __chaseOverlayHandlers?: ChaseOverlayHandlers;
  }
}

/** Wire the global ChaseRefs after the scene is built. Called from
 *  main.ts once switchScene has constructed the scene and built the
 *  refs object. The DOM overlay's handlers can then mutate the nodes
 *  via window.__chaseRefs. Returns the refs for caller convenience
 *  (truthy check, chained calls). */
export function publishChaseRefs(refs: ChaseRefs): ChaseRefs {
  window.__chaseRefs = refs;
  return refs;
}

// ─────────────────────────────────────────────────────────────────────────
// One-stop export for main.ts.
// ─────────────────────────────────────────────────────────────────────────
export const chaseDemoRoute = {
  scene: sceneChase,
  mount: mountChaseOverlay,
  unmount: unmountChaseOverlay,
  publish: publishChaseRefs,
};